import { useQuery } from '@tanstack/react-query'
import {
  BarChart3Icon,
  DollarSignIcon,
  ServerIcon,
  TrendingUpIcon,
  TrendingDownIcon,
  AlertTriangleIcon,
  CpuIcon,
  DatabaseIcon,
  HardDriveIcon,
  Loader2Icon,
  MonitorIcon,
} from 'lucide-react'
import { useState } from 'react'
import {
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'

import { ResponsivePageHeader } from '@nebula/shared/components'
import {
  getCustomerAnalytics,
  formatAnalyticsCurrency,
  getVMStatusColor,
  getVMStatusLabel,
} from '@nebula/shared/services'
import type { VMCostItem } from '@nebula/shared/services'
import { useAuthStore } from '@nebula/shared/store'

const RESOURCE_COLORS = {
  cpu: '#8B5CF6',
  memory: '#3B82F6',
  storage: '#10B981',
}

const AnalyticsPage = () => {
  // Org ativa: mesma fonte do header X-Organization-Id (auth.ts). Escopar a query
  // por org evita que ela dispare antes da org estar resolvida — o que mandava a
  // request sem o header e fazia o backend cair na org default (não-EC), retornando
  // total_vms=0 (tela "Crie sua primeira VM"). Tambem evita cache cruzado ao trocar de org.
  const activeOrgId = useAuthStore((s) => s.activeOrganizationId)
  const [autoRefresh, setAutoRefresh] = useState(true)

  const {
    data: analytics,
    isLoading,
    error,
    refetch,
    dataUpdatedAt,
  } = useQuery({
    queryKey: ['customer-analytics', activeOrgId],
    queryFn: getCustomerAnalytics,
    enabled: !!activeOrgId,
    refetchInterval: autoRefresh ? 30_000 : false,
    staleTime: 60_000,
    retry: 2,
  })

  // --- Loading (inclui o intervalo em que a org ativa ainda nao resolveu:
  // com enabled:false o React Query nao fica em isLoading, entao sem este guard
  // a tela de erro piscaria antes do primeiro fetch) ---
  if (isLoading || !activeOrgId) {
    return (
      <div className="mobile-page-padding">
        <ResponsivePageHeader title="Analytics de Custos" icon={BarChart3Icon} />
        <div className="flex items-center justify-center py-32">
          <Loader2Icon className="size-8 animate-spin text-purple-600" />
        </div>
      </div>
    )
  }

  // --- Error ---
  if (error || !analytics) {
    return (
      <div className="mobile-page-padding">
        <ResponsivePageHeader title="Analytics de Custos" icon={BarChart3Icon} />
        <div className="flex flex-col items-center justify-center space-y-4 py-32">
          <AlertTriangleIcon className="size-12 text-red-500" />
          <p className="text-gray-600">Erro ao carregar dados de analytics</p>
          <button
            onClick={() => refetch()}
            className="rounded-lg bg-purple-600 px-4 py-2 text-white hover:bg-purple-700"
          >
            Tentar novamente
          </button>
        </div>
      </div>
    )
  }

  // --- Empty (0 VMs) ---
  if (analytics.summary.total_vms === 0) {
    return (
      <div className="mobile-page-padding">
        <ResponsivePageHeader title="Analytics de Custos" icon={BarChart3Icon} />
        <div className="flex flex-col items-center justify-center space-y-4 py-32">
          <MonitorIcon className="size-12 text-gray-400" />
          <p className="text-gray-600">Crie sua primeira VM para ver analytics de custos</p>
        </div>
      </div>
    )
  }

  const { summary, cost_by_resource, billing_history, vm_costs } = analytics

  const trendIsDown = summary.cost_trend_percent <= 0
  const trendColor = trendIsDown ? 'text-green-600' : 'text-red-600'
  const trendBg = trendIsDown ? 'bg-green-100' : 'bg-red-100'

  const pieData = [
    { name: 'CPU', value: cost_by_resource.cpu.cost_estimate, color: RESOURCE_COLORS.cpu },
    {
      name: 'Memória',
      value: cost_by_resource.memory.cost_estimate,
      color: RESOURCE_COLORS.memory,
    },
    {
      name: 'Storage',
      value: cost_by_resource.storage.cost_estimate,
      color: RESOURCE_COLORS.storage,
    },
  ]

  const costPerCpu =
    cost_by_resource.cpu.total > 0
      ? cost_by_resource.cpu.cost_estimate / cost_by_resource.cpu.total
      : 0
  const costPerGbRam =
    cost_by_resource.memory.total_gb > 0
      ? cost_by_resource.memory.cost_estimate / cost_by_resource.memory.total_gb
      : 0
  const costPerGbStorage =
    cost_by_resource.storage.total_gb > 0
      ? cost_by_resource.storage.cost_estimate / cost_by_resource.storage.total_gb
      : 0

  return (
    <div className="mobile-page-padding">
      <ResponsivePageHeader
        title="Analytics de Custos"
        description="Análise de custos, eficiência e distribuição de recursos"
        icon={BarChart3Icon}
        actions={
          <div className="flex flex-col items-end gap-1">
            <button
              onClick={() => setAutoRefresh(!autoRefresh)}
              className={`inline-flex items-center rounded-lg px-3 py-1.5 text-xs font-medium ${
                autoRefresh ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
              }`}
            >
              Auto-refresh {autoRefresh ? 'ligado' : 'desligado'}
            </button>
            <div className="text-[10px] text-gray-500">
              Última atualização:{' '}
              {dataUpdatedAt ? new Date(dataUpdatedAt).toLocaleTimeString('pt-BR') : '--:--:--'}
            </div>
          </div>
        }
      />

      <div className="space-y-6">
        {/* Section 1: KPI Cards */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {/* Custo Mensal */}
          <div className="rounded-lg border border-gray-200 bg-white p-6">
            <div className="flex items-center justify-between">
              <div className="rounded-lg bg-purple-100 p-3">
                <DollarSignIcon className="size-6 text-purple-600" />
              </div>
              {summary.cost_trend_percent !== 0 && (
                <span
                  className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${trendBg} ${trendColor}`}
                >
                  {trendIsDown ? (
                    <TrendingDownIcon className="mr-1 size-3" />
                  ) : (
                    <TrendingUpIcon className="mr-1 size-3" />
                  )}
                  {Math.abs(summary.cost_trend_percent)}%
                </span>
              )}
            </div>
            <div className="mt-4">
              <h3 className="text-2xl font-bold text-gray-900">
                {formatAnalyticsCurrency(summary.total_monthly_cost)}
              </h3>
              <p className="text-sm text-gray-600">Custo Mensal</p>
            </div>
          </div>

          {/* VMs Ativas */}
          <div className="rounded-lg border border-gray-200 bg-white p-6">
            <div className="flex items-center justify-between">
              <div className="rounded-lg bg-blue-100 p-3">
                <ServerIcon className="size-6 text-blue-600" />
              </div>
            </div>
            <div className="mt-4">
              <h3 className="text-2xl font-bold text-gray-900">{summary.active_vms}</h3>
              <p className="text-sm text-gray-600">
                VMs Ativas{' '}
                <span className="text-gray-400">
                  · {summary.billable_vms} faturáveis de {summary.total_vms} total
                </span>
              </p>
            </div>
          </div>

          {/* Custo Medio/VM */}
          <div className="rounded-lg border border-gray-200 bg-white p-6">
            <div className="flex items-center justify-between">
              <div className="rounded-lg bg-green-100 p-3">
                <TrendingUpIcon className="size-6 text-green-600" />
              </div>
            </div>
            <div className="mt-4">
              <h3 className="text-2xl font-bold text-gray-900">
                {formatAnalyticsCurrency(summary.avg_cost_per_vm)}
              </h3>
              <p className="text-sm text-gray-600">
                Custo Médio/VM <span className="text-gray-400">faturável</span>
              </p>
            </div>
          </div>

          {/* Recursos Ociosos */}
          <div
            className={`rounded-lg border bg-white p-6 ${summary.idle_vms > 0 ? 'border-orange-300' : 'border-gray-200'}`}
          >
            <div className="flex items-center justify-between">
              <div
                className={`rounded-lg p-3 ${summary.idle_vms > 0 ? 'bg-orange-100' : 'bg-gray-100'}`}
              >
                <AlertTriangleIcon
                  className={`size-6 ${summary.idle_vms > 0 ? 'text-orange-600' : 'text-gray-400'}`}
                />
              </div>
            </div>
            <div className="mt-4">
              <h3 className="text-2xl font-bold text-gray-900">
                {formatAnalyticsCurrency(summary.idle_cost)}
              </h3>
              <p className="text-sm text-gray-600">
                Recursos Ociosos{' '}
                {summary.idle_vms > 0 && (
                  <span className="font-medium text-orange-600">
                    {summary.idle_vms} VMs paradas
                  </span>
                )}
              </p>
            </div>
          </div>
        </div>

        {/* Section 2: Billing History Chart */}
        <div className="rounded-lg border border-gray-200 bg-white p-6">
          <h3 className="mb-6 text-lg font-semibold text-gray-900">Evolução de Custos</h3>
          {billing_history.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={billing_history}>
                <defs>
                  <linearGradient id="colorAmount" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month_label" />
                <YAxis tickFormatter={(value) => `R$${value}`} />
                <Tooltip
                  formatter={(value) => [formatAnalyticsCurrency(Number(value ?? 0)), 'Custo']}
                />
                <Area
                  type="monotone"
                  dataKey="amount"
                  stroke="#8B5CF6"
                  strokeWidth={2}
                  fill="url(#colorAmount)"
                  name="Custo"
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 text-gray-400">
              <BarChart3Icon className="mb-2 size-10" />
              <p>Sem histórico de billing ainda</p>
            </div>
          )}
        </div>

        {/* Section 3: Resource Distribution + Efficiency */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Pie Chart */}
          <div className="rounded-lg border border-gray-200 bg-white p-6">
            <h3 className="mb-6 text-lg font-semibold text-gray-900">
              Distribuição por Recurso
              {cost_by_resource.estimated && (
                <span
                  className="ml-2 text-xs font-normal text-amber-600"
                  title="Algumas VMs usam preço de bundle (sem preço por recurso); o rateio é estimado pelas taxas de referência."
                >
                  estimativa
                </span>
              )}
            </h3>
            <div className="flex justify-center">
              <ResponsiveContainer width="100%" height={320}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={65}
                    outerRadius={95}
                    dataKey="value"
                    labelLine={false}
                    label={({ name, percent }: any) => `${name} ${(percent * 100).toFixed(0)}%`}
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => formatAnalyticsCurrency(Number(value ?? 0))} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-4 space-y-2">
              {pieData.map((item, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <div className="size-3 rounded-full" style={{ backgroundColor: item.color }} />
                    <span className="text-sm text-gray-600">{item.name}</span>
                  </div>
                  <span className="text-sm font-medium">{formatAnalyticsCurrency(item.value)}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Efficiency Cards */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Eficiência por Recurso
              {cost_by_resource.estimated && (
                <span
                  className="ml-2 text-xs font-normal text-amber-600"
                  title="Algumas VMs usam preço de bundle (sem preço por recurso); o rateio é estimado pelas taxas de referência."
                >
                  estimativa
                </span>
              )}
            </h3>
            <div className="flex items-center gap-4 rounded-lg border border-gray-200 bg-white p-5">
              <div className="rounded-lg bg-purple-100 p-3">
                <CpuIcon className="size-6 text-purple-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-gray-600">Custo por vCPU</p>
                <p className="text-xl font-bold text-gray-900">
                  {formatAnalyticsCurrency(costPerCpu)}
                </p>
              </div>
              <p className="text-sm text-gray-500">{cost_by_resource.cpu.total} vCPUs</p>
            </div>
            <div className="flex items-center gap-4 rounded-lg border border-gray-200 bg-white p-5">
              <div className="rounded-lg bg-blue-100 p-3">
                <DatabaseIcon className="size-6 text-blue-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-gray-600">Custo por GB RAM</p>
                <p className="text-xl font-bold text-gray-900">
                  {formatAnalyticsCurrency(costPerGbRam)}
                </p>
              </div>
              <p className="text-sm text-gray-500">{cost_by_resource.memory.total_gb} GB</p>
            </div>
            <div className="flex items-center gap-4 rounded-lg border border-gray-200 bg-white p-5">
              <div className="rounded-lg bg-green-100 p-3">
                <HardDriveIcon className="size-6 text-green-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-gray-600">Custo por GB Storage</p>
                <p className="text-xl font-bold text-gray-900">
                  {formatAnalyticsCurrency(costPerGbStorage)}
                </p>
              </div>
              <p className="text-sm text-gray-500">{cost_by_resource.storage.total_gb} GB</p>
            </div>
          </div>
        </div>

        {/* Section 4: VM Cost Ranking */}
        <div className="rounded-lg border border-gray-200 bg-white">
          <div className="border-b border-gray-200 px-6 py-4">
            <h3 className="text-lg font-semibold text-gray-900">Ranking de VMs por Custo</h3>
          </div>

          {/* Mobile Cards */}
          <div className="divide-y divide-gray-200 md:hidden">
            {vm_costs.map((vm: VMCostItem) => (
              <div key={vm.id} className="space-y-3 p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="font-medium text-gray-900">{vm.name}</div>
                  <div className="flex items-center gap-2">
                    {vm.is_idle && (
                      <span className="inline-flex items-center rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-800">
                        Ociosa
                      </span>
                    )}
                    {!vm.is_billable && (
                      <span
                        className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700"
                        title={`Cobrança ${vm.billing_status}`}
                      >
                        Não faturável
                      </span>
                    )}
                    <span
                      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${getVMStatusColor(vm.status)}`}
                    >
                      {getVMStatusLabel(vm.status)}
                    </span>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2 text-sm">
                  <div>
                    <span className="text-gray-500">CPU</span>
                    <p className="font-medium">{vm.cpu} vCPU</p>
                  </div>
                  <div>
                    <span className="text-gray-500">RAM</span>
                    <p className="font-medium">{vm.ram_gb} GB</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Storage</span>
                    <p className="font-medium">{vm.storage_gb} GB</p>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-lg font-bold text-gray-900">
                    {formatAnalyticsCurrency(vm.monthly_cost)}
                  </span>
                  <span className="text-sm text-gray-500">
                    {vm.is_billable ? `${vm.percentage_of_total}% do total` : 'não cobrada'}
                  </span>
                </div>
                <div className="h-1.5 w-full rounded-full bg-gray-200">
                  <div
                    className={`h-1.5 rounded-full ${vm.is_billable ? 'bg-purple-500' : 'bg-gray-300'}`}
                    style={{
                      width: `${vm.is_billable ? Math.min(vm.percentage_of_total, 100) : 0}%`,
                    }}
                  />
                </div>
              </div>
            ))}
          </div>

          {/* Desktop Table */}
          <div className="hidden overflow-x-auto md:block">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase">
                    VM
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase">
                    CPU
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase">
                    RAM
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase">
                    Storage
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase">
                    Custo Mensal
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase">
                    % do Total
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {vm_costs.map((vm: VMCostItem) => (
                  <tr key={vm.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">{vm.name}</span>
                        {vm.is_idle && (
                          <span className="inline-flex items-center rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-800">
                            Ociosa
                          </span>
                        )}
                        {!vm.is_billable && (
                          <span
                            className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700"
                            title={`Cobrança ${vm.billing_status}`}
                          >
                            Não faturável
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${getVMStatusColor(vm.status)}`}
                      >
                        {getVMStatusLabel(vm.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm whitespace-nowrap text-gray-600">
                      {vm.cpu} vCPU
                    </td>
                    <td className="px-6 py-4 text-sm whitespace-nowrap text-gray-600">
                      {vm.ram_gb} GB
                    </td>
                    <td className="px-6 py-4 text-sm whitespace-nowrap text-gray-600">
                      {vm.storage_gb} GB
                    </td>
                    <td className="px-6 py-4 text-sm font-medium whitespace-nowrap text-gray-900">
                      {formatAnalyticsCurrency(vm.monthly_cost)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {vm.is_billable ? (
                        <div className="flex items-center gap-3">
                          <div className="h-2 w-24 rounded-full bg-gray-200">
                            <div
                              className="h-2 rounded-full bg-purple-500"
                              style={{ width: `${Math.min(vm.percentage_of_total, 100)}%` }}
                            />
                          </div>
                          <span className="text-sm text-gray-600">{vm.percentage_of_total}%</span>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">não cobrada</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AnalyticsPage
